import type { ProjectClaim } from '../../types'
import ClaimSupportChips from './ClaimSupportChips'
import CombinationClaimBadge from './CombinationClaimBadge'
import ClaimScorePill from './ClaimScorePill'

/**
 * One claim in the Claims lens list (figure S1).
 *
 * Three states the figure distinguishes and this row keeps distinct:
 *   * the FOCUSED claim — the one the drawer beside it is about;
 *   * a COMBINATION claim — green, because no single document asserts it;
 *   * a claim with NO supporting document — its support line carries the
 *     warning, which is the whole reason a claim-shaped view exists.
 */
export interface ClaimRowProps {
  claim: ProjectClaim
  /** 1-based position, which is the number the reader and the URL both use. */
  index: number
  documentNames?: Record<string, string>
  focused?: boolean
  onFocus?: () => void
}

export default function ClaimRow({ claim, index, documentNames, focused, onFocus }: ClaimRowProps) {
  const isCombination = claim.supporting_document_ids.length > 1
  const border = focused
    ? 'border-iflow-500 bg-iflow-50 ring-4 ring-iflow-100'
    : isCombination
      ? 'border-emerald-600/60 bg-emerald-50/60'
      : 'border-sky-200 bg-white hover:border-iflow-300'
  const chip = isCombination
    ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
    : 'border-iflow-200 bg-iflow-50 text-iflow-700'

  return (
    <button
      type="button"
      onClick={onFocus}
      aria-current={focused ? 'true' : undefined}
      className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${border}`}
    >
      <span className={`flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-lg border p-1 text-[11.5px] font-bold ${chip}`}>
        {index}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold leading-snug text-iflow-900">{claim.claim}</span>
        <ClaimSupportChips
          supportingDocumentIds={claim.supporting_document_ids}
          documentNames={documentNames}
          trailing={
            isCombination ? <CombinationClaimBadge /> : focused ? <b className="text-iflow-700">focused</b> : null
          }
        />
      </span>
      <ClaimScorePill score={claim.novelty_score} />
    </button>
  )
}
